import { useEffect } from "react";
import { Link } from "wouter";
import { PageMeta } from "@/components/PageMeta";
import { VEGETARIAN_ARTICLE as ARTICLE } from "@/lib/vegetarianArticle";

export default function VegetarianCambodianFoodArticle() {
  const canonical = `https://www.angkorflavours.com.au${ARTICLE.path}`;
  const image = `https://www.angkorflavours.com.au${ARTICLE.image}`;
  useEffect(() => {
    let tag = document.head.querySelector<HTMLMetaElement>(
      'meta[property="og:type"]'
    );
    const created = !tag;
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("property", "og:type");
      document.head.appendChild(tag);
    }
    tag.content = "article";
    return () => {
      if (created) tag?.remove();
      else if (tag) tag.content = "website";
    };
  }, []);
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${canonical}#article`,
    headline: ARTICLE.headline,
    datePublished: ARTICLE.datePublished,
    dateModified: ARTICLE.datePublished,
    image,
    mainEntityOfPage: canonical,
    author: { "@type": "Organization", name: "ANGKOR team" },
    publisher: { "@type": "Organization", name: "ANGKOR Cambodian Flavours" },
  };
  return (
    <article className="blog-article">
      <PageMeta
        title={ARTICLE.title}
        description={ARTICLE.description}
        path={ARTICLE.path}
        image={image}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <section className="intent-hero">
        <div className="container intent-hero-grid">
          <div>
            <Link href="/blog" className="eyebrow blog-back-link">
              Blog
            </Link>
            <h1>{ARTICLE.headline}</h1>
            <p className="blog-byline">
              <time dateTime={ARTICLE.datePublished}>{ARTICLE.dateLabel}</time>{" "}
              · {ARTICLE.byline}
            </p>
          </div>
          <img
            src={ARTICLE.image}
            srcSet={ARTICLE.imageSrcSet}
            sizes="(max-width: 767px) calc(100vw - 32px), 50vw"
            alt={ARTICLE.imageAlt}
            width="1600"
            height="2000"
            fetchPriority="high"
            loading="eager"
          />
        </div>
      </section>
      <section className="section parchment-section">
        <div className="container">
          <div className="blog-article-body">
            <p>
              {
                "Cambodian food has a reputation for fish sauce, prawns and grilled meats, so vegetarians sometimes assume there is little for them on a Khmer menu. In fact, Cambodian cooking is full of vegetables, herbs, coconut, rice and fresh fruit, and many of the dishes we love most can be enjoyed without meat. Here is a simple guide to eating vegetarian, and in some cases vegan, at ANGKOR in West Melbourne."
              }
            </p>
            <h2>{"Why Cambodian food suits plant-based eaters"}</h2>
            <p>
              {
                "At the heart of Khmer cooking is kroeung, a fragrant paste of lemongrass, galangal, kaffir lime leaf, turmeric and garlic pounded together. It gives curries, stir-fries and fried rice their perfume and depth, so a dish built around tofu and vegetables still tastes full and satisfying."
              }
            </p>
            <p>
              {
                "Cambodian meals also lean on balance rather than heavy richness: something sour, something fresh, something sweet and something savoury on the same table. Green papaya, banana blossom, sweet potato, eggplant and green beans all appear across the menu, and coconut cream rounds out curries and desserts. Buddhist traditions in Cambodia mean meat-free eating is familiar too, especially on religious days."
              }
            </p>
            <h2>{"Vegetarian dishes on our dinner menu"}</h2>
            <p>
              <strong>{"Traditional Red Curry with tofu ($25.50)."}</strong>
              {
                " Our Cambodian kroeung curry in coconut broth with sweet potatoes, eggplant, green beans and herbs, with a choice of tofu or chicken. Choose tofu and it becomes one of the most comforting vegetarian plates in the house. Add Steamed Jasmine Rice ($5.00) or Asian Roti ($6.90) to soak up the sauce."
              }
            </p>
            <p>
              <strong>{"Lemongrass Fried Rice with tofu ($23.50)."}</strong>
              {
                " Wok-fried rice with homemade kroeung, egg, omelette and seasonal vegetables, made with tofu by default. It contains egg, so it suits vegetarians rather than vegans."
              }
            </p>
            <p>
              <strong>{"Cambodian Papaya Salad ($21.90)."}</strong>
              {
                " Shredded green papaya, carrot, shallots and peanuts with a sharp lime dressing. The classic version uses fish sauce, and a vegan option is available, so just ask when you order."
              }
            </p>
            <p>
              <strong>{"Spring Rolls ($11.50)."}</strong>
              {
                " Five crisp rolls with a vegetable filling and sweet chilli sauce, an easy starter for the whole table."
              }
            </p>
            <p>
              <strong>{"Banana Blossom Salad ($23.90)."}</strong>
              {
                " Spiced banana blossom with carrot, Asian herbs and crushed peanuts. It is a uniquely Southeast Asian ingredient, with a delicate crunch, and well worth trying. Ask our team how it can be prepared for your diet."
              }
            </p>
            <p>
              <strong>{"Chinese Broccoli ($14.90)."}</strong>
              {" A simple side of greens that rounds out any shared table."}
            </p>
            <h2>{"Vegetarian options at lunch"}</h2>
            <p>
              {"Visiting for lunch on a weekday? Our "}
              <Link href="/lunch">{"lunch menu"}</Link>
              {
                " includes a Red Curry Bowl with tofu and jasmine rice ($19.90) and Lemongrass Fried Rice with tofu ($18.90), both quick, filling choices if you work nearby or are on your way to an event."
              }
            </p>
            <h2>{"Finish with something sweet"}</h2>
            <p>
              {
                "Cambodian desserts are a gift to vegetarians. Try Sticky Rice with Mango ($16.00), coconut rice with fresh mango and toasted mung beans; Black Rice Pudding ($15.90) with coconut cream and sesame; or Homemade Coconut Ice Cream ($14.50) with a crispy coconut roll. Read more in our "
              }
              <Link href="/blog/cambodian-desserts-melbourne">
                {"guide to Cambodian desserts"}
              </Link>
              {"."}
            </p>
            <h2>{"A vegetarian table for two"}</h2>
            <p>
              {
                "Cambodian meals are shared, with dishes in the middle and rice for everyone. For two vegetarians, we suggest Spring Rolls and Papaya Salad to start, Red Curry with tofu and Lemongrass Fried Rice as mains with jasmine rice or roti, and Sticky Rice with Mango to finish. It gives you crunch, freshness, warmth and sweetness, the full Cambodian balance."
              }
            </p>
            <h2>{"Dietary needs and allergies"}</h2>
            <p>
              {
                "Please tell our team about any dietary needs or allergies when you order, so we can guide you to the right dishes. Our kitchen uses peanuts, sesame, gluten, milk, fish, shellfish and molluscs, egg, soy and coconut, so we cannot guarantee an allergen-free meal, and some sauces and pastes may contain fish or shrimp products. For group events, vegetarian, vegan and gluten-free alternatives are available at no extra cost with 7 days' notice. See "
              }
              <Link href="/groups-functions">{"Groups & Functions"}</Link>
              {" for details."}
            </p>
            <p>
              {"Prices are taken from our live "}
              <Link href="/menu">{"dinner menu"}</Link>
              {
                " and can change, so please check the current menu when you visit."
              }
            </p>
            <h2>{"Opening hours and location"}</h2>
            <p>
              <strong>{"ANGKOR Cambodian Flavours"}</strong>
              <br />
              {"389 Spencer Street, West Melbourne VIC 3003"}
              <br />
              {"Phone: (03) 7034 8353"}
              <br />
              {"A short walk from Marvel Stadium and Southern Cross Station."}
            </p>
            <ul>
              <li>{"Monday: closed"}</li>
              <li>
                {"Tuesday to Thursday: 11:30am to 3:00pm and 4:30pm to 9:30pm"}
              </li>
              <li>{"Friday: 11:30am to 9:30pm"}</li>
              <li>{"Saturday: 12:00pm to 9:30pm"}</li>
              <li>{"Sunday: 4:30pm to 9:30pm"}</li>
            </ul>
            <p>
              <Link href="/location">{"Directions and full hours"}</Link>
            </p>
            <h2>{"Book a table or order"}</h2>
            <p>
              {
                "Whether you are vegetarian, vegan or simply eating more plants, there is a warm Cambodian meal waiting for you. New to Khmer food? Start with our "
              }
              <Link href="/blog/khmer-family-recipes-melbourne">
                {"family recipes story"}
              </Link>
              {"."}
            </p>
            <p>
              <a href="https://reserve.oddle.me/en_AU/angkorcafe">
                {"Book a Table"}
              </a>
              {"  ·  "}
              <a href="/order">{"Order Takeaway"}</a>
              {"  ·  "}
              <Link href="/menu">{"View the Dinner Menu"}</Link>
            </p>
          </div>
        </div>
      </section>
    </article>
  );
}
